/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Divider } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useOrderDetails } from "./saloon-order-display/hooks/useOrderDetails";
import OrderHeader from "./saloon-order-display/components/OrderHeader";
import OrderSummary from "./saloon-order-display/components/OrderSummary";
import OrderItemsTable from "./saloon-order-display/components/OrderItemsTable";
import PaymentDialog from "./saloon-order-display/components/PaymentDialog";
import CancelOrderDialog from "./saloon-order-display/components/CancelDialog";
import { useCancelHandler } from "./saloon-order-display/hooks/useCancelHandler";
import { usePaymentHandler } from "./saloon-order-display/hooks/usePaymentHandler";
import OrderActions from "./saloon-order-display/components/OrderActions";
import { PrintData } from "src/print-configuration/PrintConfigration";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

const SaloonOrderDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { orderid } = useParams();
  const { order, isLoading, outstandingAmount, fetchOrderDetails, updateOrderStatus } = useOrderDetails(orderid);
  const [printOpen, setPrintOpen] = useState<boolean>(false);
  const { paymentData, openPaymentDialog, setOpenPaymentDialog, isProcessing, handlePaymentInputChange, processPayment, resetPaymentData } = usePaymentHandler(orderid, order, outstandingAmount, fetchOrderDetails);
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails[0].orderedOn.$date ?? "");
  const { cancelData, openCancelDialog, setOpenCancelDialog, handleCancelOrder, setCancelData } = useCancelHandler(updateOrderStatus);
  const tn = order?.orders[0];
  const customerName = (order?.customerDetails?.firstName ?? "") + (order?.customerDetails?.middleName ?? "") + (order?.customerDetails?.lastName ?? "");
  const subTotals = Number(tn?.orderAmount ? Number(tn?.finalAmount) : Number(tn?.orderAmount));
  const { sidebarThemeClass } = useUserTheme();

  const formattedProducts = order?.orderDetails[0]?.products.map((p: any) => ({
    productName: p.productName || p.name || "",
    arabicword: p.arabicName || "",
    amount: Number(p.unitPrice || p.price || p.amount || 0),
    quantity: Number(p.quantity || 1),
    totalPrice: Number(p.lineTotal || p.itemTotal || Number(p.unitPrice || p.price || 0) * Number(p.quantity || 1) || 0),
  }));

  // PAYMENT HISTORY
const paymentHistory = order?.paymentHistory || [];

// CURRENT PAID
const paid =
  paymentHistory.length > 0
    ? Number(
        paymentHistory[paymentHistory.length - 1]?.receivedAmount || 0
      )
    : 0;

// PREVIOUS PAID
const prevPaid =
  paymentHistory.length > 1
    ? paymentHistory
        .slice(0, -1)
        .reduce(
          (sum, item) =>
            sum + Number(item?.receivedAmount || 0),
          0
        )
    : 0;

// TOTAL PAID
const totalPaid = prevPaid + paid;

// REMAINING AMOUNT
const remainingAmountValue =
  subTotals - totalPaid > 0
    ? subTotals - totalPaid
    : 0;
    // PREVIOUS BALANCE
const prevBalance =
  subTotals - prevPaid > 0
    ? subTotals - prevPaid
    : 0;

  const printData: PrintData = {
  CustomerName: customerName || "",
  CustomerPhon: order?.customerDetails.phones[0].phone || "",
  OrderId: tn?.orderId ?? "",
  OrderStatus: tn?.status || "",
  discount: Number(tn?.discount),
  Date: orderDate || "",
  Time: orderTime || "",
  Products: formattedProducts,

  // CURRENT PAID
  Paid: paid,

  // PREVIOUS PAID
  prvPaid: prevPaid,
  
  // PREVIOUS BALANCE
  prvBalance: prevBalance,

  // TOTAL REMAINING
  unpaid: remainingAmountValue,

  // OUTSTANDING
  OSB: Number(outstandingAmount),

  paymentMethod:
    paymentHistory[paymentHistory.length - 1]?.paymentMethod || "",

  total: Number(tn?.orderAmount),

  subtotal: Number(tn?.finalAmount) || 0,

  balanceReturned: Number(tn?.balanceReturned) || 0,
};

  const handleGoBack = useCallback(() => {
    setOpenPaymentDialog(false);
    setOpenCancelDialog(false);
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    document.title = "Order › Details › iMetanic";
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white rounded-xl shadow-lg p-8">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-600 text-lg">Order not found</p>
        </div>
      </div>
    );
  }

  const remainingAmount = Number(tn?.remainingAmount) || 0;

  return (
    <div className="h-full p-1.5">
      <div className="w-full mx-auto">
        <div className={`w-full h-full flex flex-col shadow-inner rounded-lg overflow-hidden ${sidebarThemeClass}`}>
          {/* Header Section */}
          <div className="w-full p-1">
          <OrderHeader order={order} outstandingAmount={outstandingAmount} onPrint={() => setPrintOpen(!printOpen)} onGoBack={handleGoBack} printData={printData} />
          </div>

          <Divider sx={{ my: 1 }} />

          {/* Main Content */}
          <div className="p-1.5 sm:p-1.5 lg:p-1.5">
            <div className="flex flex-col lg:flex-row gap-2 lg:gap-2">
              {/* Order Items Section - 60% on large screens */}
              <div className="lg:w-[60%]">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">Order Items</h3>
                <OrderItemsTable order={order} />
              </div>

              {/* Order Summary Section - 40% on large screens */}
              <div className="lg:w-[40%]">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">Order Summary</h3>
                <OrderSummary order={order} />
              </div>
            </div>

            <Divider sx={{ my: 4 }} />

            {/* Actions Section */}
            {remainingAmount === 0 ? (
              <div className="flex justify-end mt-4">
                <div className="bg-green-50 border border-green-200 rounded-lg px-6 py-3">
                  <p className="text-green-700 font-medium flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    This order has been fully paid
                  </p>
                </div>
              </div>
            ) : (
              <OrderActions order={order} onOpenPayment={() => setOpenPaymentDialog(true)} onOpenCancel={() => setOpenCancelDialog(true)} />
            )}
          </div>
        </div>
      </div>

      <PaymentDialog
        open={openPaymentDialog}
        onClose={resetPaymentData}
        order={order}
        paymentData={paymentData}
        onPaymentDataChange={handlePaymentInputChange}
        onProcessPayment={processPayment}
        isProcessing={isProcessing}
      />

      <CancelOrderDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        order={order}
        outstandingAmount={outstandingAmount}
        cancelData={cancelData}
        onCancelDataChange={setCancelData}
        onConfirmCancel={handleCancelOrder}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SaloonOrderDisplay;
